import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import Card from '@welldev-group/rcl-card';
import IconButton from '@welldev-group/rcl-icon-button';
import { Avatar, I18nText } from '@welldev-group/react-comp-lib';
import Alert from '@welldev-group/rcl-alert';
import EditTable from './EditTable';
import DateTime from '../DateTime';
import routes from '../../../shared/routes';
import ConfirmationModal from '../../../shared/components/ConfirmationModal';

const ListTable = props => {
  const [resendInProgress, setResendInProgress] = useState({});
  const [verificationInProgress, setVerificationInProgress] = useState({});
  const [alertType, setAlertType] = useState('success');
  const [deleteInProgress, setDeleteInProgress] = useState({});
  const [reactivateInProgress, setReactivateInProgress] = useState({});
  const [flashText, setFlashText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const {
    users,
    affiliateId,
    fetchUsers,
    displayInvites,
    resendInvite,
    deleteUser,
    reactivateUser,
    permissionOptions,
    permissionTemplateOptions,
    currentUserId,
    verifyEmail,
    reverifyEmail,
    fetchUsersByAffiliate,
  } = props;

  const mapUserTeams = teams =>
    teams.map(team => ({
      value: team.id,
      label: team.name,
    }));

  const resetUserListsForVerificationSuccess = user => {
    setVerificationInProgress(prevState => ({
      ...prevState,
      [user.id]: false,
    }));
    setFlashText(
      I18n.t('components.user.actions.verified', {
        name: user.name,
      }),
    );
    setAlertType('success');
  };

  const resetUserListsForVerificationFailure = (user, error) => {
    setVerificationInProgress(prevState => ({
      ...prevState,
      [user.id]: false,
    }));
    setFlashText(error);
    fetchUsersByAffiliate();
    setAlertType('danger');
  };

  const processVerification = (user, reverification = false) => {
    setFlashText('');
    setVerificationInProgress(prevState => ({
      ...prevState,
      [user.id]: true,
    }));

    let verificationAction;
    if (reverification) {
      verificationAction = reverifyEmail;
    } else {
      verificationAction = verifyEmail;
    }
    verificationAction(user.id)
      .then(({ data }) => {
        if (data.error === null) {
          resetUserListsForVerificationSuccess(user);
        }
      })
      .catch(({ response }) => {
        if (response.status === 422) {
          resetUserListsForVerificationFailure(user, response.data.error);
        }
      });
  };

  const processResendingInvitation = user => {
    setFlashText('');
    setResendInProgress(prevState => ({
      ...prevState,
      [user.id]: true,
    }));
    resendInvite(user.id).then(() => {
      setResendInProgress(prevState => ({
        ...prevState,
        [user.id]: false,
      }));
      setFlashText(
        I18n.t('components.user.status.resend_invite_successful', {
          name: user.name,
        }),
      );
      setAlertType('success');
    });
  };

  const onUserDelete = user => {
    setFlashText('');
    setDeleteInProgress(prevState => ({
      ...prevState,
      [user.id]: true,
    }));
    deleteUser(user.id).then(({ data }) => {
      setFlashText(data.message);
      setAlertType('success');
      setDeleteInProgress(prevState => ({
        ...prevState,
        [user.id]: false,
      }));
    });
  };

  const onUserReactivate = user => {
    setFlashText('');
    setReactivateInProgress(prevState => ({
      ...prevState,
      [user.id]: true,
    }));
    reactivateUser(user.id).then(({ data }) => {
      setFlashText(data.message);
      setAlertType('success');
      setReactivateInProgress(prevState => ({
        ...prevState,
        [user.id]: false,
      }));
    });
  };

  const handleUserDelete = user => {
    setUserToDelete(user);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      onUserDelete(userToDelete);
    }

    setIsModalOpen(false);
    setUserToDelete(null);
  };

  const handleRejectDelete = () => {
    setIsModalOpen(false);
    setUserToDelete(null);
  };

  return (
    <div className="grid align-center user-index__list-table">
      <Card version="v2" size="full">
        <Alert
          className="user-index__flash col-grid direction-row col-12"
          isVisible={!!flashText}
          type={alertType}
          hideClose={true}
        >
          {flashText}
        </Alert>

        {users.length > 0 ? (
          <>
            <div className="user-index__list-headers hidden-md hidden-sm hidden-xs hidden-xxs grid">
              <div className="col-12 col-bleed">
                <div className="col-3">
                  <div className="user-index__list-name">
                    <I18nText id="user.list.header.name" />
                  </div>
                </div>
                <div className="user-index__list-email col-3">
                  <I18nText id="user.list.header.email" />
                </div>
                <div className="user-index__list-teams col-2">
                  <I18nText id="user.list.header.teams" />
                </div>
                {!displayInvites && (
                  <div className="user-index__list-last-active col-1">
                    <I18nText id="user.list.header.last_active" />
                  </div>
                )}
                {displayInvites && (
                  <div className="user-index__list-invited-at col-2">
                    <I18nText id="user.list.header.invited_at" />
                  </div>
                )}
                {!displayInvites && (
                  <div className="user-index__list-status col-2">
                    <I18nText id="user.list.header.status" />
                  </div>
                )}
                <div className="col-1 user-index__list-actions">
                  <I18nText id="user.list.header.actions" />
                </div>
              </div>
            </div>
            <div className="user-index__list-values">
              {users.map(user => (
                <Card version="v2" size="full" key={user.id}>
                  <div className="user-index__list-row grid">
                    <div className="col-lg-12 col-grid direction-row wrap col-bleed">
                      <div className="col-lg-3 col-md-4 col-sm-8 col-8 order-lg-1 order-md-1 order-sm-1 order-xs-1 user-index__list-name">
                        <Avatar
                          src={user.avatar}
                          firstName={user.firstName}
                          lastName={user.lastName}
                          size="tiny"
                        />
                        <div>
                          {user.name}
                          <div className="user-index__header user-index__header--small">
                            <I18nText id="user.list.header.name" />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-2 col-md-4 col-sm-4 col-4 order-lg-2 order-md-3 order-sm-2 order-xs-2 col-bleed user-index__list-actions user-index__list-data--middle-align">
                        <div className="col-12 col-bleed-x">
                          {displayInvites && (
                            <IconButton
                              version="v2"
                              isIconOnly={true}
                              icon={<Icon name="sendMessage" />}
                              onClick={() => processResendingInvitation(user)}
                              size="large"
                              label={
                                <I18nText id="user.actions.resend_invite" />
                              }
                              disabled={resendInProgress[user.id]}
                            />
                          )}
                          {!displayInvites && (
                            <>
                              {user.verifiable && (
                                <IconButton
                                  version="v2"
                                  isIconOnly={true}
                                  icon={<Icon name="check" />}
                                  onClick={() => processVerification(user)}
                                  size="large"
                                  label={<I18nText id="user.actions.verify" />}
                                  disabled={verificationInProgress[user.id]}
                                />
                              )}
                              {user.reVerifiable && (
                                <IconButton
                                  version="v2"
                                  isIconOnly={true}
                                  icon={<Icon name="done" />}
                                  onClick={() =>
                                    processVerification(user, true)
                                  }
                                  size="large"
                                  label={
                                    <I18nText id="user.actions.reverify" />
                                  }
                                  disabled={verificationInProgress[user.id]}
                                />
                              )}
                            </>
                          )}
                          <EditTable
                            currentUserId={currentUserId}
                            affiliateId={affiliateId}
                            userInitialAttributes={user}
                            previousSelectedTeam={mapUserTeams(user.teams)}
                            permissionOptions={permissionOptions}
                            permissionTemplateOptions={
                              permissionTemplateOptions
                            }
                            fetchUsers={fetchUsers}
                            buttonText={
                              <IconButton
                                isIconOnly={true}
                                icon={<Icon name="edit" />}
                                size="large"
                                label={
                                  user.superAdmin &&
                                  user.id !== currentUserId ? (
                                    <I18nText id="user.actions.edit.super_admin" />
                                  ) : (
                                    <I18nText id="user.actions.edit.user" />
                                  )
                                }
                                disabled={
                                  user.id === currentUserId
                                    ? false
                                    : user.superAdmin
                                }
                              />
                            }
                            submitURL={routes.users.update}
                            buttonClass="user-index__edit"
                          />
                          {user.deactivated ? (
                            <IconButton
                              version="v2"
                              isIconOnly={true}
                              icon={<Icon name="powerOff" />}
                              size="large"
                              onClick={() => onUserReactivate(user)}
                              disabled={
                                reactivateInProgress[user.id] ||
                                user.superAdmin ||
                                user.id === currentUserId
                              }
                              label={<I18nText id="user.actions.reactivate" />}
                            />
                          ) : (
                            <IconButton
                              version="v2"
                              isIconOnly={true}
                              icon={<Icon name="delete" />}
                              size="large"
                              onClick={() => handleUserDelete(user)}
                              disabled={
                                deleteInProgress[user.id] ||
                                user.superAdmin ||
                                user.id === currentUserId
                              }
                              label={
                                user.superAdmin ? (
                                  <I18nText id="user.actions.delete.super_admin" />
                                ) : (
                                  <I18nText id="user.actions.delete.user" />
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                      <div className="user-index__list-email col-lg-3 col-md-4 col-sm-6 col-12 order-lg-1 order-md-2 order-sm-3 order-xs-3 user-index__list-data--middle-align">
                        {user.email}
                        <div className="user-index__header user-index__header--small">
                          <I18nText id="user.list.header.email" />
                        </div>
                      </div>
                      <div className="user-index__list-teams col-lg-2 col-md-4 col-sm-6 col-12 order-lg-1 order-md-4 order-sm-4 order-xs-4 user-index__list-data--middle-align">
                        {user.teamsAsSentence || (
                          <I18nText id="user.list.unassigned" />
                        )}
                        <div className="user-index__header user-index__header--small">
                          <I18nText id="user.list.header.teams" />
                        </div>
                      </div>
                      {!displayInvites && (
                        <div className="user-index__list-last-active col-lg-1 col-md-4 col-sm-6 col-12 order-lg-1 order-md-4 order-sm-4 order-xs-4 user-index__list-data--middle-align">
                          {user.lastActive ? (
                            <DateTime dateTime={user.lastActive} />
                          ) : (
                            <I18nText id="user.list.not_available" />
                          )}
                          <div className="user-index__header user-index__header--small">
                            <I18nText id="user.list.header.last_active" />
                          </div>
                        </div>
                      )}
                      {displayInvites && (
                        <div className="user-index__list-invited-at col-lg-1 col-md-4 col-sm-6 col-12 order-lg-1 order-md-4 order-sm-4 order-xs-4 user-index__list-data--middle-align">
                          <DateTime dateTime={user.invitedAt} format="short" />
                          <div className="user-index__header user-index__header--small">
                            <I18nText id="user.list.header.invited_at" />
                          </div>
                        </div>
                      )}
                      {!displayInvites && (
                        <div className="user-index__list-status col-lg-1 col-md-4 col-sm-6 col-12 order-lg-1 order-md-4 order-sm-4 order-xs-4 user-index__list-data--middle-align">
                          {user.deactivated ? (
                            <I18nText id="user.list.status.deactivated" />
                          ) : (
                            <I18nText id="user.list.status.active" />
                          )}
                          <div className="user-index__header user-index__header--small">
                            <I18nText id="user.list.header.status" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <div className="col-12">
            <I18nText id="user.list.no_user" />
          </div>
        )}
      </Card>
      <ConfirmationModal
        isModalOpen={isModalOpen}
        confirmationHeader={<I18nText id="user.actions.delete.text" />}
        confirmationText={<I18nText id="user.actions.delete.user" />}
        rejectionText={<I18nText id="user.actions.cancel" />}
        onConfirm={handleConfirmDelete}
        onReject={handleRejectDelete}
        onOutsideClick={handleRejectDelete}
        type="danger"
      />
    </div>
  );
};

ListTable.defaultProps = {
  displayInvites: false,
  permissionTemplateOptions: [],
};

ListTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      avatar: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      teamsAsSentence: PropTypes.string.isRequired,
      lastActive: PropTypes.string.isRequired,
      deactivated: PropTypes.bool.isRequired,
      verifiable: PropTypes.bool,
      reVerifiable: PropTypes.bool,
    }).isRequired,
  ).isRequired,
  resendInvite: PropTypes.func.isRequired,
  verifyEmail: PropTypes.func.isRequired,
  reverifyEmail: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  reactivateUser: PropTypes.func.isRequired,
  displayInvites: PropTypes.bool,
  fetchUsersByAffiliate: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  affiliateId: PropTypes.number.isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  currentUserId: PropTypes.number.isRequired,
};

export default ListTable;
